import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const milestones = [
  { year: "2016", title: "品牌创立", text: "台北娜娜在台北东区的一间小工作室诞生，从一件大衣开始讲述都市生活的故事。" },
  { year: "2018", title: "首家旗舰门店", text: "首间品牌门店开幕，将产品、空间与生活方式体验带到线下。" },
  { year: "2020", title: "生活方式频道上线", text: "官网新增生活方式内容，从美食、旅行到居家，延伸品牌的生活主张。" },
  { year: "2022", title: "可持续计划启动", text: "发布首个环保材质系列，推动再生面料与循环设计。" },
  { year: "2024", title: "潮流趋势频道上线", text: "建立趋势研究团队，为读者带来专业的流行预测与解读。" },
  { year: "2026", title: "信义旗舰店开幕", text: "迄今规模最大的线下空间启用，融合零售、咖啡与展览功能。" },
];

const values = [
  {
    icon: "ri-focus-3-line",
    title: "专业",
    text: "以研究的态度对待每一个内容，让资讯值得信赖。",
  },
  {
    icon: "ri-heart-3-line",
    title: "真诚",
    text: "不追逐流量噪音，只分享我们真正认同的生活方式。",
  },
  {
    icon: "ri-leaf-line",
    title: "可持续",
    text: "把环保从口号变成日常选择，对人与环境同样负责。",
  },
];

const team = [
  { name: "林知远", role: "创意总监", avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20creative%20director%20in%20minimal%20neutral%20outfit%2C%20soft%20studio%20lighting%2C%20editorial%20photography%20neutral%20background&width=600&height=600&seq=team-01&orientation=squarish" },
  { name: "苏念", role: "内容主编", avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20female%20editor%20in%20warm%20neutral%20tones%2C%20soft%20studio%20lighting%2C%20editorial%20photography%20neutral%20background&width=600&height=600&seq=team-02&orientation=squarish" },
  { name: "周予安", role: "趋势研究", avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20young%20trend%20researcher%2C%20minimal%20styling%2C%20soft%20studio%20lighting%2C%20editorial%20photography%20neutral%20background&width=600&height=600&seq=team-03&orientation=squarish" },
  { name: "陈之遥", role: "品牌设计", avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20a%20brand%20designer%20in%20minimal%20outfit%2C%20soft%20studio%20lighting%2C%20editorial%20photography%20neutral%20background&width=600&height=600&seq=team-04&orientation=squarish" },
];

const honors = [
  "2026 年度最具影响力时尚品牌",
  "2025 可持续设计创新奖",
  "2024 年度生活方式内容机构",
  "2023 城市文化贡献奖",
];

const partners = ["台北设计周", "城市生活志", "潮流观察", "可持续时尚联盟", "美学研究所"];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 台北娜娜",
    description:
      "了解台北娜娜的品牌故事、发展历程与核心理念。自 2016 年创立以来，我们持续以专业内容陪伴每一种生活方式。",
    keywords: "台北娜娜,关于我们,品牌故事,发展历程,品牌理念",
  });

  return (
    <div>
      <section className="relative overflow-hidden bg-primary-900 text-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=elegant%20minimal%20brand%20studio%20space%20with%20warm%20natural%20light%20and%20neutral%20textures%2C%20cinematic%20editorial%20architecture%20photography%20muted%20tones&width=1920&height=900&seq=about-hero-2026&orientation=landscape"
          alt="台北娜娜品牌空间"
          title="关于台北娜娜 - 品牌故事"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/70 to-primary-950/40" />
        <div className="relative px-4 pb-16 pt-28 md:px-8 md:pb-20 md:pt-32 lg:px-12">
          <nav className="flex items-center gap-2 text-xs text-background-50/60">
            <Link to="/" className="cursor-pointer transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">关于我们</span>
          </nav>
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-accent-300">About Us</p>
          <h1 className="mt-3 max-w-3xl font-heading text-3xl font-extrabold leading-tight md:text-5xl">
            我们是台北娜娜
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-50/80 md:text-base">
            一个聚焦时尚生活与潮流资讯的品牌。我们相信品质感藏在日常的细节里，也相信专业的内容能让人更从容地生活。
          </p>
        </div>
      </section>

      <section className="bg-background-50 px-4 py-14 md:px-8 md:py-20 lg:px-12">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">品牌介绍</h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
              台北娜娜创立于 2016 年，最初只是一间位于台北东区的小工作室。多年过去，我们依然坚持同一件事：用克制的设计与真诚的内容，回应当代都市人的生活需求。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              如今，品牌已发展为涵盖产品、内容与空间体验的综合品牌。官网汇集品牌动态、生活方式与潮流趋势三大频道，成为连接品牌与用户的重要窗口。
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-background-200">
            <div className="aspect-[4/3] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=minimal%20warm%20brand%20workspace%20with%20fabric%20samples%20and%20sketches%20on%20a%20wooden%20desk%2C%20soft%20daylight%2C%20editorial%20photography%20neutral%20tones&width=1200&height=900&seq=about-brand-2026&orientation=landscape"
                alt="台北娜娜工作室"
                title="台北娜娜品牌工作室"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
          {values.map((value) => (
            <div key={value.title} className="rounded-2xl border border-background-200 bg-background-100 p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                <i className={`${value.icon} text-2xl`} />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{value.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 px-4 py-14 md:px-8 md:py-20 lg:px-12">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent-600">Milestones</p>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展历程</h2>
        </div>
        <div className="relative border-l border-background-300 pl-6 md:pl-8">
          {milestones.map((item) => (
            <div key={item.year} className="relative pb-9 last:pb-0">
              <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full border-2 border-background-100 bg-accent-500 md:-left-[39px]" />
              <p className="font-heading text-xl font-extrabold text-primary-600">{item.year}</p>
              <h3 className="mt-1.5 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-50 px-4 py-14 md:px-8 md:py-20 lg:px-12">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent-600">Our Team</p>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">团队展示</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            一群热爱生活、在意细节的人，构成了台北娜娜的日常。
          </p>
        </div>
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
          {team.map((member) => (
            <div key={member.name} className="overflow-hidden rounded-2xl border border-background-200 bg-background-100">
              <div className="aspect-square w-full overflow-hidden">
                <img
                  src={member.avatar}
                  alt={member.name}
                  title={`${member.name} - 台北娜娜团队`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="p-4 text-center">
                <p className="font-heading text-base font-bold text-foreground-950">{member.name}</p>
                <p className="mt-1 text-xs text-foreground-500">{member.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 px-4 py-14 md:px-8 md:py-20 lg:px-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">品牌荣誉</h2>
            <ul className="mt-6 space-y-4">
              {honors.map((honor) => (
                <li key={honor} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-award-line text-sm" />
                  </span>
                  <span className="text-sm leading-relaxed text-foreground-700">{honor}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">合作伙伴</h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {partners.map((partner) => (
                <span
                  key={partner}
                  className="rounded-xl border border-background-300 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-700"
                >
                  {partner}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-primary-900 px-6 py-8 md:px-10">
          <div>
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">想更了解我们？</h2>
            <p className="mt-2 text-sm text-background-50/70">欢迎留言，或浏览我们的最新内容。</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              联系我们
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/30 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              浏览品牌动态
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}